import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Plus, MessageSquare, Trash2, ArrowRight, Menu, Headphones } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { useChat } from "@/hooks/useChat";
import api, { formatApiError, notifyError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ChatThread } from "@/components/ChatThread";
import { ImageAttach, ImagePreview } from "@/components/ImageAttach";
import { MicButton } from "@/components/MicButton";
import { UsageNudge } from "@/components/UsageNudge";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const MODES = ["understand", "compare", "decide", "act"];

export default function Chat() {
  const { id } = useParams();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [initial, setInitial] = useState({ loading: !!id, messages: [], id: id || null });

  const loadList = useCallback(() => {
    api.get("/ai/conversations").then(({ data }) => setConversations(data)).catch(() => setConversations([]));
  }, []);

  useEffect(() => { loadList(); }, [loadList, id]);

  useEffect(() => {
    let mounted = true;
    if (id) {
      setInitial({ loading: true, messages: [], id });
      api.get(`/ai/conversations/${id}`)
        .then(({ data }) => { if (mounted) setInitial({ loading: false, messages: data.messages || [], id }); })
        .catch(() => { if (mounted) { setInitial({ loading: false, messages: [], id: null }); navigate("/chat", { replace: true }); } });
    } else {
      setInitial({ loading: false, messages: [], id: null });
    }
    return () => { mounted = false; };
  }, [id, navigate]);

  const deleteConv = async (convId, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/ai/conversations/${convId}`);
      setConversations((prev) => prev.filter((c) => c.conversation_id !== convId));
      if (convId === id) navigate("/chat", { replace: true });
    } catch (err) {
      notifyError(err, t);
    }
  };

  const sidebar = (
    <div className="flex h-full flex-col">
      <Button onClick={() => navigate("/chat")} data-testid="new-chat-btn" className="mb-4 w-full gap-2 rounded-xl min-h-[44px]">
        <Plus className="h-4 w-4" /> {t("new_chat")}
      </Button>
      <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-slate-400">{t("recent_conversations")}</p>
      <div className="flex-1 space-y-1 overflow-y-auto" data-testid="conversation-list">
        {conversations.length === 0 ? (
          <p className="px-1 py-2 text-sm text-slate-400">{t("no_conversations")}</p>
        ) : (
          conversations.map((c) => (
            <button
              key={c.conversation_id}
              onClick={() => navigate(`/chat/${c.conversation_id}`)}
              data-testid={`conversation-item-${c.conversation_id}`}
              className={`group flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-left text-sm transition-colors ${c.conversation_id === id ? "bg-primary/10 text-primary" : "text-slate-600 hover:bg-slate-100"}`}
            >
              <MessageSquare className="h-4 w-4 flex-shrink-0 opacity-60" />
              <span className="min-w-0 flex-1 truncate">{c.title || t("new_chat")}</span>
              <span onClick={(e) => deleteConv(c.conversation_id, e)} data-testid={`delete-conversation-${c.conversation_id}`} className="flex-shrink-0 text-slate-300 opacity-60 hover:text-rose-500 sm:opacity-0 sm:group-hover:opacity-100" role="button" aria-label={t("delete_conversation")}>
                <Trash2 className="h-3.5 w-3.5" />
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <div className="mx-auto grid w-full max-w-6xl flex-1 grid-cols-1 gap-6 px-4 py-6 sm:px-6 md:grid-cols-[260px_1fr] lg:px-8">
        <aside className="hidden md:block">{sidebar}</aside>

        <div className="md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" className="gap-2 rounded-full" data-testid="chat-history-btn">
                <Menu className="h-4 w-4" /> {t("conversations")}
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[85%] max-w-sm">
              <SheetHeader><SheetTitle>{t("conversations")}</SheetTitle></SheetHeader>
              <div className="mt-6 h-[calc(100vh-8rem)]">{sidebar}</div>
            </SheetContent>
          </Sheet>
        </div>

        <main className="min-w-0">
          {initial.loading ? (
            <div className="flex h-64 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-primary" />
            </div>
          ) : (
            <ChatView key={initial.id || "new"} initialId={initial.id} initialMessages={initial.messages} onChanged={loadList} />
          )}
        </main>
      </div>
    </div>
  );
}

function ChatView({ initialId, initialMessages, onChanged }) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState("");
  const [mode, setMode] = useState("understand");
  const [images, setImages] = useState([]);
  const [voiceMode, setVoiceMode] = useState(false);
  const { messages, send, sendImage, retry, streaming, followups, conversationId } = useChat({
    initialConversationId: initialId,
    initialMessages,
  });
  const isAuthed = user && typeof user === "object";
  const composerRef = useRef(null);

  // The /chat composer is sticky at the bottom — publish its ACTUAL height so the
  // floating Help widget floats above it (never overlaps) on any viewport.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const el = composerRef.current;
    if (!el) return;
    const update = () => {
      const gap = Math.max(0, window.innerHeight - el.getBoundingClientRect().top);
      root.style.setProperty("--evermin-composer", `${gap}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, { passive: true });
    return () => { ro.disconnect(); window.removeEventListener("resize", update); window.removeEventListener("scroll", update); root.style.setProperty("--evermin-composer", "0px"); };
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    if (streaming) return;
    const clean = prompt.trim();
    if (images.length) {
      setPrompt("");
      const imgs = images;
      setImages([]);
      await sendImage(imgs, clean);
      onChanged?.();
      return;
    }
    if (!clean) return;
    setPrompt("");
    const before = conversationId;
    const newId = await send(clean, mode);
    if (!before && newId) {
      navigate(`/chat/${newId}`, { replace: true });
    }
    onChanged?.();
  };

  const saveMessage = async (msg) => {
    const userPrompt = [...messages].reverse().find((m) => m.role === "user");
    const category = msg.metadata?.category;
    try {
      await api.post("/saved-items", {
        type: "problem",
        title: (userPrompt?.content || "EVERMIN answer").slice(0, 120),
        content: msg.content,
        tags: category ? [mode, category] : [mode],
        metadata: msg.metadata || {},
      });
      toast.success(t("result_saved"));
      return true;
    } catch (err) {
      notifyError(err, t);
      return false;
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 px-6 py-20 text-center" data-testid="chat-empty">
            <MessageSquare className="h-8 w-8 text-slate-300" />
            <h2 className="font-display mt-3 text-xl font-semibold text-slate-900">{t("headline")}</h2>
            <p className="mt-1 text-sm text-slate-500">{t("brand_tagline")}</p>
          </div>
        ) : (
          <ChatThread
            messages={messages}
            onSaveMessage={saveMessage}
            isAuthed={isAuthed}
            followups={followups}
            onFollowup={(text) => send(text, mode)}
            onRetry={retry}
            streaming={streaming}
            voiceMode={voiceMode}
          />
        )}
      </div>

      <form ref={composerRef} onSubmit={submit} className="sticky bottom-0 mt-4 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
        <ImagePreview files={images} onRemove={(i) => setImages((p) => p.filter((_, idx) => idx !== i))} />
        <div className="mb-2 flex flex-wrap gap-2">
          {MODES.map((m) => (
            <button key={m} type="button" data-testid={`chat-mode-${m}`} onClick={() => setMode(m)}
              className={`min-h-[32px] rounded-full px-3 text-xs font-medium transition-colors ${mode === m ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {t(`mode_${m}`)}
            </button>
          ))}
        </div>
        <div className="flex items-end gap-2">
          <ImageAttach onSelect={(fs) => setImages((p) => [...p, ...fs].slice(0, 4))} disabled={streaming || images.length >= 4} />
          <MicButton onTranscript={(txt) => setPrompt(txt)} disabled={streaming} />
          <button type="button" onClick={() => setVoiceMode((v) => !v)} data-testid="voice-mode-toggle"
            aria-label="Voice mode" title={voiceMode ? "Voice Mode on — answers are read aloud" : "Turn on Voice Mode"}
            className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full transition-colors ${voiceMode ? "bg-primary text-white" : "text-slate-500 hover:bg-slate-100"}`}>
            <Headphones className="h-4 w-4" />
          </button>
          <textarea
            data-testid="chat-input"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) submit(e); }}
            rows={1}
            placeholder={images.length ? t("ask_about_image_ph") : t("chat_placeholder")}
            className="min-h-[48px] flex-1 resize-none rounded-xl bg-transparent px-3 py-2.5 text-base text-slate-900 placeholder:text-slate-400 focus:outline-none"
          />
          <Button type="submit" data-testid="chat-send-btn" disabled={(!prompt.trim() && !images.length) || streaming} className="h-12 rounded-xl px-4">
            {streaming ? t("asking") : <ArrowRight className="h-5 w-5" />}
          </Button>
        </div>
        <div className="mt-2 flex justify-end"><UsageNudge /></div>
      </form>
    </div>
  );
}
